var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { createA11yOutlineStyles } from '../helpers/a11y';
import { useDragHandlers } from './helpers/drag';
import { styled, useTheme } from '../theme-provider';
import { useMediaGalleryContext } from './hooks/use-media-gallery-context';
import { useGalleryControls } from './hooks/use-gallery-controls';
const StyledMediaGalleryWrapper = styled.div.withConfig({ displayName: "StyledMediaGalleryWrapper", componentId: "sc-za3i9f" }) `position:relative;width:100%;height:100%;`;
const StyledMediaGallery = styled.div.withConfig({ displayName: "StyledMediaGallery", componentId: "sc-1ohxc5m" }) `position:absolute;top:0;right:0;bottom:0;left:0;overflow:hidden;outline:none;:focus-visible{${({ theme }) => createA11yOutlineStyles(theme, 'solid')};outline-offset:unset;}`;
const StyledSlides = styled.div.attrs(({ slidePosition, theme: { direction } }) => ({
    style: {
        transform: direction === 'rtl'
            ? `translate3d(${slidePosition}%, 0, 0)`
            : `translate3d(-${slidePosition}%, 0, 0)`,
    },
})).withConfig({ displayName: "StyledSlides", componentId: "sc-2k505" }) `display:flex;width:${({ length }) => length * 100}%;height:100%;transition:${({ animate, theme }) => animate
    ? `transform ${theme.animation.duration.layer} ${theme.animation.timingFunction.in}`
    : 'none'};`;
export const Carousel = (_a) => {
    var { children, onChange } = _a, rest = __rest(_a, ["children", "onChange"]);
    const { direction } = useTheme();
    const { state, dispatch, getState } = useMediaGalleryContext();
    const { changeSlide, slideToNextSlide, slideToPreviousSlide } = useGalleryControls(onChange);
    const [slides, virtualSlides] = React.useMemo(() => {
        const slides = React.Children.toArray(children).filter(React.isValidElement);
        const length = slides.length;
        if (length > 1) {
            const leadingCopy = React.cloneElement(slides[length - 1], {
                key: `${slides[length - 1].key}-copy`,
            });
            const trailingCopy = React.cloneElement(slides[0], {
                key: `${slides[0].key}-copy`,
            });
            const virtualslides = [leadingCopy, ...slides, trailingCopy];
            return [slides, virtualslides];
        }
        return [slides, slides];
    }, [children]);
    React.useEffect(() => {
        const slides = React.Children.toArray(children).filter(React.isValidElement);
        const length = slides.length;
        dispatch({ type: 'SET_SLIDE_LENGTH', length });
        const slideKeys = slides.map((slide) => slide.key);
        dispatch({ type: 'SET_SLIDE_KEYS', slideKeys });
    }, [children, dispatch]);
    const slidesContainerRef = React.useRef(null);
    const carouselRef = React.useRef(null);
    const onStart = React.useCallback(() => {
        dispatch({ type: 'DRAG_START', position: 0 });
    }, [dispatch]);
    const onMove = React.useCallback((newXPosition, event) => {
        var _a;
        const { lastIndex } = state;
        const offset = direction === 'rtl' ? newXPosition - event.x : event.x - newXPosition;
        const percentageOffset = offset / (((_a = carouselRef.current) === null || _a === void 0 ? void 0 : _a.clientWidth) || window.innerWidth);
        const newIndex = Math.min(Math.max(lastIndex + percentageOffset, -1), slides.length + 1);
        dispatch({ type: 'DRAG_MOVE', index: newIndex });
    }, [state, slides, dispatch, direction]);
    const handleEnd = React.useCallback(() => {
        const { dragStartTime, lastIndex, activeIndex, dragEnabled } = getState();
        const timeElapsed = new Date().getTime() - dragStartTime;
        const speed = Math.abs((activeIndex / timeElapsed) * 10000);
        let newIndex = Math.round(activeIndex);
        if (dragEnabled) {
            // don't wait to swipe half of the slide;
            // go to the nex slide instead if the swiping speed was fast enough
            if (speed > 20 && activeIndex !== lastIndex) {
                newIndex = activeIndex < lastIndex ? lastIndex - 1 : lastIndex + 1;
                newIndex = Math.min(Math.max(newIndex, -1), slides.length + 1);
            }
            changeSlide(newIndex);
        }
    }, [changeSlide, slides, getState]);
    const handleDragStart = useDragHandlers(onMove, onStart, handleEnd);
    React.useEffect(() => {
        const { animate, activeIndex } = state;
        if (animate) {
            const duration = slidesContainerRef.current
                ? parseFloat(getComputedStyle(slidesContainerRef.current).getPropertyValue('transition-duration')) * 1000
                : 0;
            setTimeout(() => {
                if (activeIndex === -1) {
                    changeSlide(slides.length - 1, false);
                }
                else if (activeIndex === slides.length) {
                    changeSlide(0, false);
                }
            }, duration);
        }
    }, [changeSlide, slides, state, state.activeIndex]);
    React.useEffect(() => {
        const container = carouselRef.current;
        if (container && slides.length > 1) {
            // events need to be marked as not passive to prevent scrolling when needed
            container.addEventListener('touchstart', handleDragStart, {
                passive: false,
            });
        }
        return () => {
            if (container) {
                container.removeEventListener('touchstart', handleDragStart, false);
            }
        };
    }, [handleDragStart, slides]);
    function onKeyDown(e) {
        if (e.key === 'ArrowRight') {
            slideToNextSlide(e);
        }
        else if (e.key === 'ArrowLeft') {
            slideToPreviousSlide(e);
        }
    }
    return (React.createElement(StyledMediaGalleryWrapper, Object.assign({}, rest),
        React.createElement(StyledMediaGallery, { tabIndex: 0, ref: carouselRef, onKeyDown: onKeyDown },
            React.createElement(StyledSlides, { animate: state.animate, length: virtualSlides.length, slidePosition: state.slidePosition, ref: slidesContainerRef }, virtualSlides))));
};
